import React, { forwardRef, useEffect, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import styled from "styled-components";

const Link = Quill.import("formats/link");

export const toolbarId = "quill-editor-toolbar";

const DEFAULT_LINK_COLOR = "black";

export const modules = {
  toolbar: {
    container: `#${toolbarId}`,
  },
};

export const formats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "link",
];

interface Props {
  quill: ReactQuill | null;
  linkColor?: string;
  showHeading?: boolean;
}

export const EditorToolbar = forwardRef<HTMLDivElement, Props>(
  ({ quill, linkColor = DEFAULT_LINK_COLOR, showHeading = true }, ref) => {
    const [format, setFormat] = useState<Record<string, any> | null>(null);

    const isHeadingSelected = format?.size === "large";
    const isHeadingDisabled =
      format?.bold || format?.italic || format?.underline;

    useEffect(() => {
      const editor = quill?.getEditor();
      const updateFormat = () => {
        const selection = editor?.getSelection();
        selection &&
          setFormat(editor?.getFormat(selection.index, selection.length));
      };
      editor?.on("selection-change", updateFormat);
      editor?.on("text-change", updateFormat);

      return () => {
        editor?.off("selection-change", updateFormat);
        editor?.off("text-change", updateFormat);
      };
    }, [quill]);

    useEffect(() => {
      class StyledLink extends Link {
        static create(value) {
          let node = super.create(value);
          node.setAttribute("href", this.sanitize(value));
          node.setAttribute("target", "_blank");
          node.setAttribute("style", `--color: ${linkColor};`);
          return node;
        }
      }

      Quill.register(StyledLink, true);
    }, [linkColor]);

    return (
      <Container
        ref={ref}
        id={toolbarId}
        data-is-heading-disabled={isHeadingDisabled}
      >
        {showHeading && (
          <select className="ql-size" defaultValue="normal">
            <option value="large">Heading</option>
            <option value="normal">Body</option>
          </select>
        )}
        <button className="ql-bold" disabled={isHeadingSelected} />
        <button className="ql-italic" disabled={isHeadingSelected} />
        <button className="ql-underline" disabled={isHeadingSelected} />
        <button className="ql-link" />
      </Container>
    );
  },
);

const Container = styled.div`
  border-radius: 7px 7px 0 0;

  && {
    border-bottom: none;
  }

  button:disabled {
    pointer-events: none;

    path,
    line {
      stroke: var(--disabled-color) !important;
    }

    rect {
      fill: var(--disabled-color) !important;
    }
  }

  &[data-is-heading-disabled="true"] {
    span[data-value="large"] {
      color: var(--disabled-color) !important;
      pointer-events: none;
    }
  }
`;
